import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { setDays } from '@/redux/bookingSlice';
import CalendarIcon from '@/assets/icons/calendar.svg?react';
import arrowDown from '@/assets/icons/arrow-down.svg';
import useClickOutside from '@/hooks/useClickOutside';
import getSelectedPlan from '@/utils/getSelectedPlan';
import usePlan from '@/hooks/usePlan';
import styles from './selectors.module.scss';
import PlanSVG from '../planSVG/PlanSVG';

interface IProps {
    location?: string;
}

const DaysSelector = ({ location = 'home' }: IProps) => {
    const [isActive, setIsActive] = useState(false);
    const days = useAppSelector((state) => state.booking.days);
    const isRedeemingAGift = useAppSelector((state) => state.gift.isRedeemingAGift);
    const isOfferDiscount = useAppSelector((state) => state.discount.isOfferDiscount);
    const { planConfig, planAdminData, planMinDays } = usePlan();
    const { isMultiDestinationPlan, isAllSurprisePlan, isAloyPlan } = getSelectedPlan(planConfig.id);
    const multiDestinationFlights = (days - 1) / 2;
    const selectorRef = useRef<HTMLDivElement>(null);
    const isHomePage = location === 'home';
    const isGiftTravelPage = location === 'giftTravel';

    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    useLayoutEffect(() => {
        if (isRedeemingAGift) return;
        dispatch(setDays(Number(planMinDays)));
    }, [planConfig.id, planMinDays]);

    const onSelectorClick = () => {
        setIsActive(!isActive);
    };

    const onItemClick = (item: any) => {
        dispatch(setDays(Number(item)));
        setIsActive(false);
    };

    const getContainerStyles = () => {
        if (isHomePage) return `${styles.daysSelectorMulti}`;
        if (!isHomePage) return styles.fullWidth;
        return null;
    };

    const getDaysArray = () => {
        const daysToShow = planAdminData?.days ?? [];
        if (isOfferDiscount || isGiftTravelPage) return daysToShow.filter((day) => day >= 3);
        return daysToShow;
    };

    useClickOutside(selectorRef, () => {
        if (isActive) setIsActive(false);
    });

    const daysArray = getDaysArray();
    const selectorClassName = isActive ? `${styles.selector} ${styles.selectorActive}` : styles.selector;
    const labelText = isHomePage ? t('days') : t('howManyDays');
    const normalDays = isHomePage ? days : `${days} ${t('days')}`;
    const multidestinationDays = isHomePage
        ? `${days}/${multiDestinationFlights}`
        : `${days} ${t('days')} / ${multiDestinationFlights} ${t('destinations').toLowerCase()}`;

    return (
        <div
            className={`${styles.selectorContainer} ${styles.daysSelector} ${
                !isAloyPlan && styles.daysSelectorFlykube
            } ${getContainerStyles()}`}
            ref={selectorRef}
        >
            <p className={styles.selectorName}>{labelText}</p>
            <button className={selectorClassName} onClick={onSelectorClick} disabled={isAllSurprisePlan}>
                <PlanSVG svg={<CalendarIcon />} className={styles.icon} />
                <p className={styles.text}>{isMultiDestinationPlan ? multidestinationDays : normalDays}</p>

                {!isAllSurprisePlan && (
                    <div className={styles.arrow}>
                        <img src={arrowDown} alt="" className={isActive ? styles.arrowActive : ''} />
                    </div>
                )}
            </button>
            {isActive && <SelectorOptions itemsList={daysArray} onItemClick={onItemClick} isHomePage={isHomePage} />}
        </div>
    );
};

interface IOptionsProps {
    itemsList: any;
    onItemClick: any;
    isHomePage: boolean;
}

const SelectorOptions = ({ itemsList, onItemClick, isHomePage }: IOptionsProps) => {
    const { planConfig } = usePlan();
    const { isMultiDestinationPlan } = getSelectedPlan(planConfig.id);
    const isMultiInHome = isHomePage && isMultiDestinationPlan;
    const { t } = useTranslation();

    return (
        <div
            className={`${styles.selectorOptions} ${isHomePage && styles.daysSelectorOptions} ${
                isMultiInHome && styles.selectorOptionsMulti
            }`}
        >
            <ul>
                {itemsList?.map((item: any, index: number) => {
                    const flightsNumber = (item - 1) / 2;
                    const nights = item - 1;
                    const daysText = `${item} ${t('days')}`;
                    const nightsText = `${nights} ${nights > 1 ? t('nights') : t('night')}`;
                    const selectorText = `${daysText} / ${nightsText}`;
                    const multidestinationText = `${item} ${t('days')} / ${flightsNumber} ${t(
                        'destinations',
                    ).toLowerCase()}`;

                    return (
                        <li key={item}>
                            <button onClick={() => onItemClick(item)} tabIndex={index}>
                                {isMultiDestinationPlan ? multidestinationText : selectorText}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default DaysSelector;
